import { GROUP_SELECTION_STEP } from './intakeFlowVariant';
import type { IntakeAction, IntakeState, TherapyBooking } from './types';
import {
  getActiveTherapyTypes,
  getProviderSelectionTherapyTypes,
  groupModalityToFormat,
  isGroupOnlyIntake,
  TOTAL_STEPS,
} from './types';

/** Group selection lives outside the 0–13 range, so it must survive the SET_STEP clamp. */
const MAX_STEP_INDEX = Math.max(TOTAL_STEPS - 1, GROUP_SELECTION_STEP);

export function intakeReducer(state: IntakeState, action: IntakeAction): IntakeState {
  switch (action.type) {
    case 'SET_THERAPY_TYPES': {
      const therapyTypes = action.payload;
      const keepsGroup = therapyTypes.includes('group');
      // A format derived from a cohort was never chosen by the client, so drop it once the flow
      // stops being Group-only and the format step becomes reachable again.
      const dropDerivedFormat = !keepsGroup && isGroupOnlyIntake(state.answers);
      return {
        ...state,
        answers: {
          ...state.answers,
          therapyTypes,
          therapyType: therapyTypes[0] ?? null,
          ...(keepsGroup
            ? {}
            : {
                groupSelection: 'none' as const,
                requestedGroupId: undefined,
                requestedGroupTemplateId: undefined,
                requestedGroupName: undefined,
                ...(dropDerivedFormat ? { format: null } : {}),
              }),
        },
      };
    }
    case 'SET_THERAPY_TYPE':
      return { ...state, answers: { ...state.answers, therapyType: action.payload } };
    case 'REMOVE_THERAPY_TYPE': {
      const nextTypes = state.answers.therapyTypes.filter((t) => t !== action.payload);
      const nextRound =
        state.currentBookingRound >= nextTypes.length
          ? Math.max(0, nextTypes.length - 1)
          : state.currentBookingRound;
      return {
        ...state,
        currentBookingRound: nextRound,
        answers: {
          ...state.answers,
          therapyTypes: nextTypes,
          therapyType: nextTypes[0] ?? null,
        },
      };
    }
    case 'SET_THERAPY_TYPE_CANCELLED': {
      const { therapyType, cancelled } = action.payload;
      const prev = state.answers.cancelledTherapyTypes ?? [];
      const nextCancelled = cancelled
        ? prev.includes(therapyType) ? prev : [...prev, therapyType]
        : prev.filter((t) => t !== therapyType);
      const nextRound = getActiveTherapyTypes({ ...state.answers, cancelledTherapyTypes: nextCancelled }).length;
      const safeRound =
        state.currentBookingRound >= nextRound ? Math.max(0, nextRound - 1) : state.currentBookingRound;
      return {
        ...state,
        currentBookingRound: safeRound,
        answers: { ...state.answers, cancelledTherapyTypes: nextCancelled },
      };
    }
    case 'SET_THERAPY_TYPE_WAITLISTED': {
      const { therapyType, waitlisted } = action.payload;
      const prev = state.answers.waitlistedTherapyTypes ?? [];
      const nextWaitlisted = waitlisted
        ? prev.includes(therapyType) ? prev : [...prev, therapyType]
        : prev.filter((t) => t !== therapyType);
      return {
        ...state,
        answers: { ...state.answers, waitlistedTherapyTypes: nextWaitlisted },
      };
    }
    case 'SET_SKIPPED_AT_PROVIDER': {
      const { therapyType, skipped } = action.payload;
      const prev = state.answers.skippedAtProviderTherapyTypes ?? [];
      const nextSkipped = skipped
        ? prev.includes(therapyType) ? prev : [...prev, therapyType]
        : prev.filter((t) => t !== therapyType);
      return {
        ...state,
        answers: { ...state.answers, skippedAtProviderTherapyTypes: nextSkipped },
      };
    }
    case 'RESTORE_LAST_SKIPPED_AT_PROVIDER': {
      const pending = [...(state.answers.skippedAtProviderTherapyTypes ?? [])];
      const restored = pending.pop();
      if (!restored) return state;
      const answersAfter = {
        ...state.answers,
        skippedAtProviderTherapyTypes: pending,
      };
      const providerTypes = getProviderSelectionTherapyTypes(answersAfter);
      const roundIdx = providerTypes.indexOf(restored);
      return {
        ...state,
        currentBookingRound: roundIdx >= 0 ? roundIdx : 0,
        answers: answersAfter,
      };
    }
    case 'SET_SESSION_TYPE':
      return { ...state, answers: { ...state.answers, sessionType: action.payload } };
    case 'SET_TOPICS':
      return { ...state, answers: { ...state.answers, topics: action.payload } };
    case 'SET_SPECIALTIES':
      return { ...state, answers: { ...state.answers, specialties: action.payload } };
    case 'SET_DEMOGRAPHIC_PREFS':
      return { ...state, answers: { ...state.answers, demographicPrefs: action.payload } };
    case 'SET_LANGUAGE':
      return { ...state, answers: { ...state.answers, language: action.payload } };
    case 'SET_SCREENING':
      return { ...state, answers: { ...state.answers, [action.payload.field]: action.payload.value } };
    case 'SET_FORMAT':
      return { ...state, answers: { ...state.answers, format: action.payload } };
    case 'SET_AVAILABILITY_TIME_OF_DAY':
      return { ...state, answers: { ...state.answers, availabilityTimeOfDay: action.payload } };
    case 'SET_AVAILABILITY_DAYS':
      return { ...state, answers: { ...state.answers, availabilityDays: action.payload } };
    case 'SET_FIRST_NAME':
      return {
        ...state,
        answers: {
          ...state.answers,
          firstName: action.payload,
          intakeLeadPatientId: null,
          emailVerified: false,
          existingClient: false,
        },
      };
    case 'SET_LAST_NAME':
      return {
        ...state,
        answers: {
          ...state.answers,
          lastName: action.payload,
          intakeLeadPatientId: null,
          emailVerified: false,
          existingClient: false,
        },
      };
    case 'SET_EMAIL':
      return {
        ...state,
        answers: {
          ...state.answers,
          email: action.payload,
          intakeLeadPatientId: null,
          emailVerified: false,
          existingClient: false,
          emailVerificationCode: '',
        },
      };
    case 'SET_DATE_OF_BIRTH':
      return {
        ...state,
        answers: {
          ...state.answers,
          dateOfBirth: action.payload,
          intakeLeadPatientId: null,
          emailVerified: false,
          existingClient: false,
        },
      };
    case 'SET_ZIP':
      return {
        ...state,
        answers: {
          ...state.answers,
          zip: action.payload,
          intakeLeadPatientId: null,
          emailVerified: false,
          existingClient: false,
        },
      };
    case 'SET_INTAKE_LEAD_PATIENT_ID':
      return { ...state, answers: { ...state.answers, intakeLeadPatientId: action.payload } };
    case 'SET_PAY_TYPE':
      return {
        ...state,
        answers: {
          ...state.answers,
          payType: action.payload,
          ...(action.payload === 'cash'
            ? {
                insuranceCompany: null,
                insurancePayerOrgId: null,
                memberId: '',
                groupId: '',
                insuranceStartDate: '',
                insuranceEndDate: '',
                subscriberRelationship: 'self' as const,
                subscriberFirstName: '',
                subscriberLastName: '',
                subscriberDateOfBirth: '',
              }
            : {
              stripeSetupIntentId: null,
              stripePaymentMethodId: null,
              stripePaymentMethodSummary: null,
              cashPaymentArrangedLater: false,
            }),
        },
      };
    case 'SET_INSURANCE_PAYER':
      return {
        ...state,
        answers: {
          ...state.answers,
          insurancePayerOrgId: action.payload.orgId,
          insuranceCompany: action.payload.name,
        },
      };
    case 'SET_SUBSCRIBER_RELATIONSHIP':
      return { ...state, answers: { ...state.answers, subscriberRelationship: action.payload } };
    case 'SET_SUBSCRIBER_FIRST_NAME':
      return { ...state, answers: { ...state.answers, subscriberFirstName: action.payload } };
    case 'SET_SUBSCRIBER_LAST_NAME':
      return { ...state, answers: { ...state.answers, subscriberLastName: action.payload } };
    case 'SET_SUBSCRIBER_DATE_OF_BIRTH':
      return { ...state, answers: { ...state.answers, subscriberDateOfBirth: action.payload } };
    case 'SET_SELECTED_PROVIDER_ID':
      return { ...state, answers: { ...state.answers, selectedProviderId: action.payload } };
    case 'SET_BOOKING_DATE':
      return { ...state, answers: { ...state.answers, bookingDate: action.payload } };
    case 'SET_BOOKING_TIME':
      return { ...state, answers: { ...state.answers, bookingTime: action.payload } };
    case 'SET_SCREENING_SLOT':
      return {
        ...state,
        answers: {
          ...state.answers,
          screeningSlotStart: action.payload.start,
          screeningSlotEnd: action.payload.end,
          screeningPractitionerId: action.payload.practitionerId,
        },
      };
    case 'SET_MEMBER_ID':
      return { ...state, answers: { ...state.answers, memberId: action.payload } };
    case 'SET_GROUP_ID':
      return { ...state, answers: { ...state.answers, groupId: action.payload } };
    case 'SET_GROUP_THERAPY_SELECTION': {
      // Group-only skips the format step, so the cohort's modality stands in for the answer the
      // client never gets asked. Mixed requests keep whatever the client picks on that step.
      const format = isGroupOnlyIntake(state.answers)
        ? groupModalityToFormat(action.payload.modality)
        : state.answers.format;
      return {
        ...state,
        answers: {
          ...state.answers,
          groupSelection: action.payload.groupSelection,
          requestedGroupId: action.payload.requestedGroupId,
          requestedGroupTemplateId: action.payload.requestedGroupTemplateId,
          requestedGroupName: action.payload.requestedGroupName,
          format,
        },
      };
    }
    case 'SET_INSURANCE_START_DATE':
      return { ...state, answers: { ...state.answers, insuranceStartDate: action.payload } };
    case 'SET_INSURANCE_END_DATE':
      return { ...state, answers: { ...state.answers, insuranceEndDate: action.payload } };
    case 'SET_STRIPE_PAYMENT':
      return {
        ...state,
        answers: {
          ...state.answers,
          stripeSetupIntentId: action.payload.setupIntentId,
          stripePaymentMethodId: action.payload.paymentMethodId,
          stripePaymentMethodSummary: action.payload.summary,
          cashPaymentArrangedLater: false,
        },
      };
    case 'CLEAR_STRIPE_PAYMENT':
      return {
        ...state,
        answers: {
          ...state.answers,
          stripeSetupIntentId: null,
          stripePaymentMethodId: null,
          stripePaymentMethodSummary: null,
        },
      };
    case 'SET_CASH_PAYMENT_ARRANGED_LATER':
      return {
        ...state,
        answers: {
          ...state.answers,
          cashPaymentArrangedLater: action.payload,
          ...(action.payload
            ? {
              stripeSetupIntentId: null,
              stripePaymentMethodId: null,
              stripePaymentMethodSummary: null,
            }
            : {}),
        },
      };
    case 'SET_PREFERRED_LOCATION_IDS':
      return { ...state, answers: { ...state.answers, preferredLocationIds: action.payload } };
    case 'SET_EMAIL_VERIFICATION_CODE':
      return { ...state, answers: { ...state.answers, emailVerificationCode: action.payload } };
    case 'SET_EMAIL_VERIFIED':
      return { ...state, answers: { ...state.answers, emailVerified: action.payload } };
    case 'SET_EXISTING_CLIENT':
      return { ...state, answers: { ...state.answers, existingClient: action.payload } };
    case 'SET_STEP':
      return {
        ...state,
        activeStep: Math.max(0, Math.min(action.payload, MAX_STEP_INDEX)),
      };
    case 'SAVE_CURRENT_BOOKING': {
      // Use the same list the provider loop indexes (excludes screening/skipped) so the round's
      // booking + reservation map to the correct service's EpisodeOfCare.
      const activeTypes = getProviderSelectionTherapyTypes(state.answers);
      const therapyType = activeTypes[state.currentBookingRound];
      const newBooking: TherapyBooking = {
        therapyType,
        sessionType: state.answers.sessionType ?? 'intake',
        selectedProviderId: state.answers.selectedProviderId,
        bookingDate: state.answers.bookingDate,
        bookingTime: state.answers.bookingTime,
        cancelled: false,
        waitlisted: (state.answers.waitlistedTherapyTypes ?? []).includes(therapyType),
        reservation: state.answers.currentReservation ?? undefined,
      };
      return {
        ...state,
        answers: {
          ...state.answers,
          bookings: [...state.answers.bookings, newBooking],
          selectedProviderId: null,
          bookingDate: null,
          bookingTime: null,
          // Reset so the next round derives its own default from that service's intakeType
          // (StepSessionType auto-selects), instead of inheriting this round's choice.
          sessionType: undefined,
          currentReservation: null,
        },
      };
    }
    case 'SAVE_SCREENING_CALL_BOOKING': {
      const newBooking: TherapyBooking = {
        therapyType: state.answers.therapyTypes[0] ?? 'individual',
        sessionType: 'consultation',
        selectedProviderId: null,
        bookingDate: state.answers.bookingDate,
        bookingTime: state.answers.bookingTime,
        cancelled: false,
        isScreeningCall: true,
      };
      return {
        ...state,
        answers: {
          ...state.answers,
          bookings: [...state.answers.bookings, newBooking],
          bookingDate: null,
          bookingTime: null,
        },
      };
    }
    case 'POP_LAST_BOOKING': {
      const bookings = [...state.answers.bookings];
      const last = bookings.pop();
      const wasWaitlisted =
        last?.waitlisted === true ||
        (last != null && (state.answers.waitlistedTherapyTypes ?? []).includes(last.therapyType));
      const nextWaitlisted =
        wasWaitlisted && last
          ? (state.answers.waitlistedTherapyTypes ?? []).filter((t) => t !== last.therapyType)
          : state.answers.waitlistedTherapyTypes;
      return {
        ...state,
        currentBookingRound: bookings.length,
        answers: {
          ...state.answers,
          bookings,
          waitlistedTherapyTypes: nextWaitlisted,
          selectedProviderId: last?.selectedProviderId ?? null,
          bookingDate: last?.bookingDate ?? null,
          bookingTime: last?.bookingTime ?? null,
          currentReservation: last?.reservation ?? null,
        },
      };
    }
    case 'REMOVE_BOOKING': {
      const bookings = state.answers.bookings.filter((_, i) => i !== action.payload);
      return {
        ...state,
        answers: { ...state.answers, bookings },
      };
    }
    case 'SET_BOOKING_CANCELLED': {
      const { index, cancelled } = action.payload;
      const bookings = state.answers.bookings.map((b, i) =>
        i === index ? { ...b, cancelled } : b
      );
      return {
        ...state,
        answers: { ...state.answers, bookings },
      };
    }
    case 'SET_CURRENT_BOOKING_ROUND':
      return { ...state, currentBookingRound: action.payload };
    case 'SET_CURRENT_RESERVATION':
      return {
        ...state,
        answers: { ...state.answers, currentReservation: action.payload },
      };
    case 'RESET':
      return { answers: action.payload, activeStep: 0, currentBookingRound: 0 };
    case 'RESTORE_INTAKE_RESUME':
      return {
        ...state,
        answers: {
          ...state.answers,
          ...action.payload.answers,
          intakeLeadPatientId: action.payload.patientId,
        },
        activeStep: action.payload.step,
      };
    default:
      return state;
  }
}
